// Utility functions for handling user mentions in search queries

/**
 * Extracts mentions from a search query
 * @param {string} query - The search query
 * @returns {Array} Array of mention objects with { username, startIndex, endIndex }
 */
export const extractMentions = (query) => {
    const mentions = [];
    const mentionRegex = /@(\w+)/g;
    let match;

    while ((match = mentionRegex.exec(query)) !== null) {
        mentions.push({
            username: match[1],
            startIndex: match.index,
            endIndex: match.index + match[0].length,
            fullMatch: match[0]
        });
    }

    return mentions;
};

/**
 * Checks if the cursor position is within a mention
 * @param {string} query - The search query
 * @param {number} cursorPosition - Current cursor position
 * @returns {Object|null} Mention object if cursor is within a mention, null otherwise
 */
export const getMentionAtCursor = (query, cursorPosition) => {
    const mentions = extractMentions(query);
    
    for (const mention of mentions) {
        if (cursorPosition >= mention.startIndex && cursorPosition <= mention.endIndex) {
            return mention;
        }
    }
    
    return null;
};

/**
 * Checks if the cursor is at a position where a new mention could start
 * @param {string} query - The search query
 * @param {number} cursorPosition - Current cursor position
 * @returns {boolean} True if cursor is at a position where @ could start a mention
 */
export const canStartMention = (query, cursorPosition) => {
    // Check if cursor is at the beginning or after a space
    if (cursorPosition === 0) return true;
    
    const charBeforeCursor = query[cursorPosition - 1];
    return charBeforeCursor === ' ' || charBeforeCursor === '\n';
};

/**
 * Gets the search term for user lookup when @ is typed
 * @param {string} query - The search query
 * @param {number} cursorPosition - Current cursor position
 * @returns {string|null} The search term after @, or null if not a valid mention position
 */
export const getMentionSearchTerm = (query, cursorPosition) => {
    if (cursorPosition === 0) return null;
    
    // Find the @ symbol that starts the current mention
    let atSymbolIndex = cursorPosition - 1;
    while (atSymbolIndex >= 0 && query[atSymbolIndex] !== '@') {
        atSymbolIndex--;
    }
    
    // If we didn't find an @ symbol, return null
    if (atSymbolIndex < 0) return null;
    
    // Check if there's a space between @ and cursor
    for (let i = atSymbolIndex + 1; i < cursorPosition; i++) {
        if (query[i] === ' ') return null;
    }
    
    // Find the end of the current word
    let endIndex = cursorPosition;
    while (endIndex < query.length && /\w/.test(query[endIndex])) {
        endIndex++;
    }
    
    // Return the search term (everything after @ up to the end of the word)
    const searchTerm = query.substring(atSymbolIndex + 1, endIndex);
    return searchTerm; // This can be empty string for just "@"
};

/**
 * Replaces a mention in the query with the selected user
 * @param {string} query - The original query
 * @param {Object} mention - The mention object to replace
 * @param {Object} selectedUser - The selected user object
 * @returns {string} The query with the mention replaced
 */
export const replaceMention = (query, mention, selectedUser) => {
    const beforeMention = query.substring(0, mention.startIndex);
    const afterMention = query.substring(mention.endIndex);
    const username = selectedUser.username || selectedUser.name;
    
    return beforeMention + `@${username} ` + afterMention;
};

/**
 * Highlights mentions in the query for display
 * @param {string} query - The search query
 * @returns {Array} Array of text segments with mention highlighting
 */
export const highlightMentions = (query) => {
    const mentions = extractMentions(query);
    const segments = [];
    let lastIndex = 0;

    mentions.forEach((mention, index) => {
        // Add text before mention
        if (mention.startIndex > lastIndex) {
            segments.push({
                text: query.substring(lastIndex, mention.startIndex),
                type: 'text'
            });
        }

        // Add highlighted mention
        segments.push({
            text: mention.fullMatch,
            type: 'mention',
            username: mention.username
        });

        lastIndex = mention.endIndex;
    });

    // Add remaining text
    if (lastIndex < query.length) {
        segments.push({
            text: query.substring(lastIndex),
            type: 'text'
        });
    }

    return segments;
};

/**
 * Extracts usernames from mentions for API calls
 * @param {string} query - The search query
 * @returns {Array} Array of usernames mentioned in the query
 */
export const getMentionedUsernames = (query) => {
    const mentions = extractMentions(query);
    return mentions.map(mention => mention.username);
}; 